import { useQuery } from '@tanstack/react-query';
import { apiRequest } from './client';
import { queryKeys } from './queryKeys';

export type MembershipTier = 'free' | 'plus' | 'ultra' | 'founders' | 'torchbearer';
export type MembershipDuration = 'week' | 'month' | 'year' | 'custom';
export type ModelTier = 'standard' | 'blitz' | 'creative' | 'blitz_creative';

export interface Entitlements {
  tier: MembershipTier;
  tierLabel: string;
  msgs_free_daily: number;
  msgs_free_left_today: number;
  unlimited_standard: boolean;
  /** Q4-Paket: Wochenrate. Default 0 fuer 'free' / unbekannte Stufen. */
  coins_weekly: number;
  /** Q4-Paket: Jahresrate = coins_weekly * 52. Default 0. */
  coins_yearly: number;
  /** Q4-Paket: Ableitung monatlich = coins_weekly * 4. Default 0. */
  coins_monthly: number;
  /** Q4-Paket: naechste Wochen-Tranche in ISO-8601, null = keine weiteren. */
  next_coin_grant_at: string | null;
  last_tranche_granted?: boolean;
  zdr_included: boolean;
  tts: 'none' | 'minimax' | 'eleven';
  live_minutes_per_cycle: number;
  live_cycle_end: string | null;
  cycle_end: string | null;
  live_coin_per_min: number;
  live_minutes_monthly: number;
  live_minutes_left: number;
  backgrounds_monthly: number;
  backgrounds_left: number;
  themes: string[];
  icons: string[];
  /** Server-Tier-Key fuer das Abzeichen (M6). null = kein Abzeichen. */
  badge: BadgeTier | null;
}

/** M6: nur Tiere mit sichtbarem Abzeichen. */
export type BadgeTier = Extract<MembershipTier, 'plus' | 'ultra' | 'founders' | 'torchbearer'>;

export interface StoreContractFields {
  product_id?: string | null;
  base_plan_id?: string | null;
  in_trial?: boolean;
  billing_issue?: 'grace' | 'hold' | null;
  contract_ref?: string | null;
  contract_has_account_id?: boolean;
  manage_url?: string | null;
}
function storeContractFields(raw: Record<string, unknown>): StoreContractFields {
  return {
    product_id: typeof raw.product_id === 'string' ? raw.product_id : undefined,
    base_plan_id: typeof raw.base_plan_id === 'string' ? raw.base_plan_id : undefined,
    in_trial: bool(raw.in_trial),
    billing_issue: raw.billing_issue === 'grace' || raw.billing_issue === 'hold' ? raw.billing_issue : null,
    contract_ref: typeof raw.contract_ref === 'string' ? raw.contract_ref : undefined,
    contract_has_account_id: raw.contract_has_account_id === true,
    manage_url: typeof raw.manage_url === 'string' ? raw.manage_url : undefined,
  };
}
export interface ActiveMembership extends StoreContractFields {
  tier: MembershipTier;
  status: string;
  duration: MembershipDuration;
  current_period_end: string | null;
  auto_renew_enabled?: boolean | null;
  usable_until?: string | null;
  provider_product_id: string | null;
  /** Paket 75-kauf/3: 'apple' | 'google' | 'admin' | 'bestandsschutz' | ...
   *  — steuert, ob ein Wechsel über die Store-Replace-API möglich ist. */
  provider: string | null;
}

export interface MembershipSnapshot {
  googleAccountId?: string;
  entitlements: Entitlements;
  membership: ActiveMembership | null;
  /** Paket 75 (Review-Befund aus 75-kauf und 75-geschenke, ein Vertrag): die
   *  höchste aktive STORE-Mitgliedschaft (provider 'apple'/'google') aus
   *  `store_membership`, unabhängig davon, ob sie auch die oberste
   *  Mitgliedschaft insgesamt ist — eine höhere Nicht-Store-Mitgliedschaft
   *  (Admin-Vergabe/Geschenk, duration 'custom') verdeckt sonst in
   *  `membership` ein eigenes laufendes Store-Abo. Wechsel-/Konflikt-
   *  Entscheidungen der Paywall (Relation, Ersatz-Token, Trial, Laufzeitende,
   *  Cross-Store-Hinweis) beruhen darauf, NICHT auf `membership`.
   *  null = kein aktives Store-Abo; undefined = Server liefert das Feld (noch)
   *  nicht, dann gilt wie bisher `membership` (planRelation.relationMembership). */
  storeMembership?: ActiveMembership | null;
}

export interface MembershipBenefits {
  daily_turns: number;
  weekly_coins: number;
  monthly_coins: number;
  monthly_turns: number;
  live_minutes: number;
  backgrounds: number;
}

export interface MembershipPrice {
  benefits?: MembershipBenefits;
  tier: Exclude<MembershipTier, 'free' | 'torchbearer'>;
  duration: Exclude<MembershipDuration, 'custom'>;
  priceEur: number;
  formattedPrice: string | null;
  /** Catalog hint, not store-account eligibility. */
  trialAvailable: boolean;
  appleProductId: string | null;
  googleProductId: string | null;
  googleBasePlanId: string | null;
  googleTrialOfferId: string | null;
  googleDiscountOfferId: string | null;
}

export interface MessageTierPrice {
  adventure: number;
  chat: number;
}

export interface BestandsschutzState {
  state: 'pending' | 'shown' | 'claimed' | 'expired' | 'none';
  plusUntil: string | null;
  discountAvailable?: boolean;
}

const DEFAULT_ENTITLEMENTS: Entitlements = {
  tier: 'free',
  tierLabel: 'Frei',
  msgs_free_daily: 0,
  msgs_free_left_today: 0,
  unlimited_standard: false,
  coins_weekly: 0,
  coins_yearly: 0,
  coins_monthly: 0,
  next_coin_grant_at: null,
  last_tranche_granted: false,
  zdr_included: false,
  tts: 'none',
  live_minutes_per_cycle: 0,
  live_cycle_end: null,
  cycle_end: null,
  live_coin_per_min: 0,
  live_minutes_monthly: 0,
  live_minutes_left: 0,
  backgrounds_monthly: 0,
  backgrounds_left: 0,
  themes: ['fantasy', 'light', 'dark'],
  icons: ['standard'],
  badge: null,
};

const FALLBACK_PRICES: MembershipPrice[] = [
  ['plus', 2.99, 7.99, 79.99],
  ['ultra', 6.99, 19.99, 199.99],
  ['founders', 14.99, 39.99, 399.99],
].flatMap(([tier, week, month, year]) => (
  ([['week', week], ['month', month], ['year', year]] as const).map(([duration, priceEur]) => ({
    tier: tier as MembershipPrice['tier'],
    duration,
    priceEur: priceEur as number,
    formattedPrice: null,
    trialAvailable: false,
    appleProductId: `eu.fluesterfeder.app.${tier}_${duration}`,
    googleProductId: `lorath_${tier}`,
    googleBasePlanId: duration,
    googleTrialOfferId: null,
    googleDiscountOfferId: 'bestand25',
  }))
));

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function number(value: unknown, fallback = 0): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function bool(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true') return true;
  if (value === 0 || value === '0' || value === 'false') return false;
  return fallback;
}

function tier(value: unknown): MembershipTier {
  return ['plus', 'ultra', 'founders', 'torchbearer'].includes(String(value))
    ? value as MembershipTier
    : 'free';
}

// F2: Server liefert Badge-IDs praefixiert ('badge_plus', 'badge_ultra', ...;
// siehe server/membership-tiers.js). Wir akzeptieren BEIDE Formen — die
// praefigierte Form aus /api/membership (Eigenes Mitglieds-Abzeichen) UND die
// bare-Form aus entitlements.getBadgeTier (fremde User, z.B. discover.js).
function badgeTier(value: unknown): BadgeTier | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const stripped = raw.startsWith('badge_') ? raw.slice('badge_'.length) : raw;
  return ['plus', 'ultra', 'founders', 'torchbearer'].includes(stripped)
    ? stripped as BadgeTier
    : null;
}

// Paket 75: server/membership.js liefert `store_membership` in einer eigenen,
// flachen Form — ohne die Legacy-Fallbacks des `membership`-Wurzelobjekts
// weiter unten, das historisch gewachsene Client-Stub-Formen mitschleppt.
// null bleibt null (keine aktive Store-Mitgliedschaft).
function parseStoreMembership(value: unknown): ActiveMembership | null {
  if (value === null || value === undefined) return null;
  const raw = record(value);
  const parsedTier = tier(raw.tier);
  if (parsedTier === 'free') return null;
  const status = typeof raw.status === 'string' ? raw.status : 'active';
  const duration = ['week', 'month', 'year', 'custom'].includes(String(raw.duration))
    ? raw.duration as MembershipDuration
    : 'custom';
  return {
    tier: parsedTier,
    status,
    duration,
    ...storeContractFields(raw),
    current_period_end: typeof (raw.current_period_end ?? raw.period_end) === 'string' ? String(raw.current_period_end ?? raw.period_end) : null,
    auto_renew_enabled: (raw.auto_renew ?? raw.auto_renew_enabled) == null
      ? (status === 'cancelled' ? false : null)
      : bool(raw.auto_renew ?? raw.auto_renew_enabled),
    usable_until: typeof raw.usable_until === 'string' ? raw.usable_until : null,
    provider_product_id: typeof (raw.product_id ?? raw.provider_product_id) === 'string' ? String(raw.product_id ?? raw.provider_product_id) : null,
    provider: typeof raw.provider === 'string' ? raw.provider : null,
  };
}

export function normalizeMembership(value: unknown): MembershipSnapshot {
  const root = record(value);
  // Server-Vertrag (30_UMSETZUNG.md §5):
  //   { tier, label, status, period_end, provider, duration,
  //     entitlements: {...}, bestandsschutz }
  // tier/label/status/provider/duration/period_end liegen also auf der
  // Wurzel — NICHT in einem membership-Unterobjekt. Frühere Lesart
  // (`root.membership?.tier`) lieferte daher für JEDEN Zahler "free".
  const rootTier = tier(root.tier);
  const rootStatus = typeof root.status === 'string' ? root.status : null;
  const rootProvider = typeof root.provider === 'string' ? root.provider : null;
  const rootDuration = ['week', 'month', 'year', 'custom'].includes(String(root.duration))
    ? root.duration as MembershipDuration
    : null;
  const rootPeriodEnd = typeof (root.current_period_end ?? root.period_end) === 'string' ? String(root.current_period_end ?? root.period_end) : null;
  const rootProductId = typeof (root.product_id ?? root.provider_product_id) === 'string' ? String(root.product_id ?? root.provider_product_id) : null;

  const rawEntitlements = record(root.entitlements ?? root);
  // Root-Tier hat Vorrang (Server-Vertrag). Fallbacks lassen Legacy-Clients
  // und Test-Stubs, die ihre Daten in entitlements einbetten, weiter laufen.
  const normalizedTier = tier(rawEntitlements.tier ?? rootTier);
  const tierLabel = typeof rawEntitlements.tierLabel === 'string'
    ? rawEntitlements.tierLabel
    : typeof rawEntitlements.tier_label === 'string'
      ? rawEntitlements.tier_label
      : typeof root.label === 'string'
        ? root.label
        : normalizedTier;
  const themes = Array.isArray(rawEntitlements.themes)
    ? rawEntitlements.themes.filter((entry): entry is string => typeof entry === 'string')
    : DEFAULT_ENTITLEMENTS.themes;
  const icons = Array.isArray(rawEntitlements.icons)
    ? rawEntitlements.icons.filter((entry): entry is string => typeof entry === 'string')
    : DEFAULT_ENTITLEMENTS.icons;
  const tts = ['minimax', 'eleven'].includes(String(rawEntitlements.tts))
    ? rawEntitlements.tts as Entitlements['tts']
    : 'none';

  // Active-Membership-Block: kommt aus dem Root (Vertrag). Legacy-Stubs
  // konnten `root.membership` liefern — wir akzeptieren beide Formen, aber
  // die Root-Form gewinnt.
  const legacyMembership = record(root.membership ?? root.active_membership ?? root.activeMembership);
  const membershipTier = tier(rootTier !== 'free' ? rootTier : (legacyMembership.tier ?? rawEntitlements.tier));
  const membershipStatus = rootStatus
    ?? (typeof legacyMembership.status === 'string' ? legacyMembership.status : 'active');
  const membershipDuration = rootDuration
    ?? (['week', 'month', 'year', 'custom'].includes(String(legacyMembership.duration))
      ? legacyMembership.duration as MembershipDuration
      : 'custom');
  const membershipPeriodEnd = rootPeriodEnd
    ?? (typeof legacyMembership.current_period_end === 'string'
      ? legacyMembership.current_period_end
      : typeof legacyMembership.period_end === 'string'
        ? legacyMembership.period_end
        : null);
  const membershipProductId = rootProductId
    ?? (typeof legacyMembership.provider_product_id === 'string'
      ? legacyMembership.provider_product_id
      : null);

  return {
    googleAccountId: typeof root.google_account_id === 'string' ? root.google_account_id : undefined,
    entitlements: {
      ...DEFAULT_ENTITLEMENTS,
      tier: normalizedTier,
      tierLabel,
      msgs_free_daily: number(rawEntitlements.msgs_free_daily),
      msgs_free_left_today: number(rawEntitlements.msgs_free_left_today),
      unlimited_standard: bool(rawEntitlements.unlimited_standard),
      coins_weekly: number(rawEntitlements.coins_weekly),
      coins_yearly: number(rawEntitlements.coins_yearly),
      coins_monthly: number(rawEntitlements.coins_monthly),
      next_coin_grant_at: typeof rawEntitlements.next_coin_grant_at === 'string'
        ? rawEntitlements.next_coin_grant_at
        : null,
      last_tranche_granted: bool(rawEntitlements.last_tranche_granted),
      zdr_included: bool(rawEntitlements.zdr_included),
      tts,
      live_minutes_per_cycle: number(rawEntitlements.live_minutes_per_cycle),
      live_cycle_end: typeof rawEntitlements.live_cycle_end === "string" ? rawEntitlements.live_cycle_end : null,
      cycle_end: typeof rawEntitlements.cycle_end === "string" ? rawEntitlements.cycle_end : null,
      live_coin_per_min: number(rawEntitlements.live_coin_per_min),
      live_minutes_monthly: number(rawEntitlements.live_minutes_monthly),
      live_minutes_left: number(rawEntitlements.live_minutes_left),
      backgrounds_monthly: number(rawEntitlements.backgrounds_monthly),
      backgrounds_left: number(rawEntitlements.backgrounds_left),
      themes,
      icons,
      badge: rawEntitlements.badge !== undefined
        ? badgeTier(rawEntitlements.badge)
        : badgeTier(membershipTier),
    },
    membership: membershipTier === 'free' ? null : {
      tier: membershipTier,
      status: membershipStatus,
      duration: membershipDuration,
      ...storeContractFields({ ...legacyMembership, ...root }),
      current_period_end: membershipPeriodEnd,
      auto_renew_enabled: (root.auto_renew_enabled ?? legacyMembership.auto_renew_enabled) == null
        ? (membershipStatus === 'cancelled' ? false : null)
        : bool(root.auto_renew_enabled ?? legacyMembership.auto_renew_enabled),
      usable_until: typeof (root.usable_until ?? legacyMembership.usable_until) === 'string'
        ? String(root.usable_until ?? legacyMembership.usable_until)
        : (membershipStatus === 'cancelled' ||
            (root.auto_renew_enabled ?? legacyMembership.auto_renew_enabled) != null &&
            !bool(root.auto_renew_enabled ?? legacyMembership.auto_renew_enabled)) ? membershipPeriodEnd : null,
      provider_product_id: membershipProductId,
      // Paket 75-kauf/3: wurde bisher geparst (rootProvider), aber nie ins
      // Ergebnis übernommen — planRelation.isCrossStoreMembership() konnte
      // dadurch NIE einen Store-Konflikt erkennen.
      provider: rootProvider
        ?? (typeof legacyMembership.provider === 'string' ? legacyMembership.provider : null),
    },
    // Fehlt das Feld (älterer Server), bleibt es undefined → Rückfall auf
    // membership (planRelation.relationMembership); null = kein Store-Abo.
    ...('store_membership' in root ? { storeMembership: parseStoreMembership(root.store_membership && typeof root.store_membership === 'object'
      ? { contract_has_account_id: root.contract_has_account_id, ...record(root.store_membership) } : root.store_membership) } : {}),
  };
}

/** Defensiver Default-Snapshot fuer nicht-eingeloggte Aufrufer / Cache-Misses.
 *  Ohne `storeMembership` (undefined = keine Server-Angabe) — wie ein älterer
 *  Server; mit membership null ergibt relationMembership ohnehin null. */
export const DEFAULT_NORMALIZED_TIER: MembershipSnapshot = Object.freeze({
  entitlements: { ...DEFAULT_ENTITLEMENTS },
  membership: null,
});

export function normalizePrices(value: unknown): MembershipPrice[] {
  const root = record(value);
  // Server-Vertrag (30_UMSETZUNG.md §5):
  //   { tiers: [{ tier, week:{product_id, apple_product_id, google_product_id,
  //     google_base_plan_id, price_eur, store_price?}, month:{...}, year:{...},
  //     trial_days, badge }], currency }
  // Legacy-Form (war hartcodierter Fallback) hatte flache
  // { tier, duration, price_eur, ... }-Zeilen — wird weiter akzeptiert.
  let tiers: Array<Record<string, unknown>>;
  if (Array.isArray(root.tiers)) {
    tiers = (root.tiers as unknown[]).map((entry) => record(entry));
  } else if (Array.isArray(root.prices)) {
    tiers = (root.prices as unknown[]).map((entry) => record(entry));
  } else if (Array.isArray(root.products)) {
    tiers = (root.products as unknown[]).map((entry) => record(entry));
  } else if (Array.isArray(value)) {
    tiers = (value as unknown[]).map((entry) => record(entry));
  } else {
    tiers = [];
  }
  const parsed: MembershipPrice[] = [];
  for (const tierEntry of tiers) {
    const rawTier = String(tierEntry.tier ?? '');
    if (!['plus', 'ultra', 'founders'].includes(rawTier)) continue;
    // Catalog capability only; the native resolver also checks the actual store phases.
    const defaultTrial = rawTier === 'founders' ? 0 : 7;
    const trialDays = number(tierEntry.trial_days ?? tierEntry.trialDays, defaultTrial);
    const explicitTrialAvailable = bool(tierEntry.trial_available ?? tierEntry.trialAvailable, false);
    const tierAllowsTrial = rawTier !== 'founders' && trialDays > 0;
    for (const durationKey of ['week', 'month', 'year'] as const) {
      const slot = record(tierEntry[durationKey] ?? {});
      if (Object.keys(slot).length === 0) continue;
      // trialAvailable: explizit (Default false) ∧ Tier erlaubt Trial ∧
      // trial_days > 0. So bleibt ein `trial_available:false` aus dem Server
      // IMMER false, auch fuer Plus/Ultra.
      const trialAvailable = explicitTrialAvailable && tierAllowsTrial;
      parsed.push({
        tier: rawTier as MembershipPrice['tier'],
        benefits: tierEntry.benefits as MembershipBenefits | undefined,
        duration: durationKey,
        priceEur: number(slot.price_eur ?? slot.priceEur),
        formattedPrice: typeof slot.formatted_price === 'string'
          ? slot.formatted_price
          : typeof slot.formattedPrice === 'string'
            ? slot.formattedPrice
            : typeof slot.store_price === 'string'
              ? slot.store_price
              : null,
        trialAvailable,
        appleProductId: typeof slot.apple_product_id === 'string'
          ? slot.apple_product_id
          : typeof slot.product_id === 'string'
            ? slot.product_id
            : null,
        googleProductId: typeof slot.google_product_id === 'string' ? slot.google_product_id : null,
        googleBasePlanId: typeof slot.google_base_plan_id === 'string'
          ? slot.google_base_plan_id
          : durationKey,
        // Offer identifiers are hints, not evidence of store eligibility.
        googleTrialOfferId: trialAvailable
          ? (typeof slot.google_trial_offer_id === 'string'
            ? slot.google_trial_offer_id
            : 'trial7')
          : null,
        googleDiscountOfferId: typeof slot.google_discount_offer_id === 'string'
          ? slot.google_discount_offer_id
          : 'bestand25',
      });
    }
  }
  if (!parsed.length) return FALLBACK_PRICES;
  return FALLBACK_PRICES.map((fallback) => (
    parsed.find((item) => item.tier === fallback.tier && item.duration === fallback.duration) ?? fallback
  ));
}

export function normalizeMessageTierPrices(value: unknown, modelTier: ModelTier, zdr: boolean): MessageTierPrice {
  const root = record(value);
  // F2: Serverform ist { adventure: { standard,blitz,creative,blitz_creative },
  // chat: { … }, zdr_surcharge } — also Flaechen-zuerst, Tier darunter (siehe
  // server/model-tiers.js getMessageTiersPayload()). Wir lesen pro Flaeche den
  // Tier-Unter-Key, NICHT einen vermeintlichen Top-Level-Tier-Key.
  const source = record(root.message_tiers ?? root.costs ?? root);
  const surfaceAdventure = record(source.adventure);
  const surfaceChat = record(source.chat);
  const tierPrices = record(surfaceAdventure[modelTier] ?? surfaceChat[modelTier]);
  const surcharge = zdr ? number(root.zdr_surcharge ?? source.zdr_surcharge, 0) : 0;
  const adventure = number(tierPrices.adventure ?? tierPrices.campaign, modelTier === 'creative' ? 15 : modelTier === 'blitz_creative' ? 8 : modelTier === 'blitz' ? 2 : 1);
  const chat = number(tierPrices.chat ?? tierPrices.character, modelTier === 'creative' ? 5 : modelTier === 'blitz_creative' ? 3 : modelTier === 'blitz' ? 2 : 1);
  return { adventure: adventure + surcharge, chat: chat + surcharge };
}

export async function getMembership(): Promise<MembershipSnapshot> {
  return normalizeMembership(await apiRequest<unknown>('/api/membership'));
}

export async function getMembershipPrices(): Promise<MembershipPrice[]> {
  return normalizePrices(await apiRequest<unknown>('/api/membership/prices'));
}

export async function getMessageTierPrices(): Promise<unknown> {
  // Server-Vertrag §5: /api/coins/costs liefert ein gemeinsames Antwortobjekt,
  // aus dem wir message_tiers lesen. Der frühere Pfad
  // '/api/coins/costs.message_tiers' war ein Tippfehler und lieferte 404.
  // message_tiers existiert noch nicht (M3 offen) — normalizeMessageTierPrices
  // fällt in dem Fall auf seine hartcodierten Defaults zurück, bis M3 merged.
  return apiRequest<unknown>('/api/coins/costs');
}

export async function getBestandsschutz(): Promise<BestandsschutzState> {
  const raw = record(await apiRequest<unknown>('/api/membership/bestandsschutz'));
  const state = ['pending', 'shown', 'claimed', 'expired'].includes(String(raw.state))
    ? raw.state as BestandsschutzState['state']
    : 'none';
  return { state, plusUntil: typeof raw.plus_until === 'string' ? raw.plus_until : null, discountAvailable: raw.discount_available === true };
}

export function acknowledgeBestandsschutz(action: 'seen' | 'claim'): Promise<unknown> {
  return apiRequest(`/api/membership/bestandsschutz/${action}`, { method: 'POST' });
}

export interface BestandsschutzOffer {
  apple: { productId: string; offerIdentifier: string; keyIdentifier: string; nonce: string; signature: string | null; timestamp: number } | null;
  google: { productId: string; basePlanId: string; offerId: string } | null;
}
export function prepareBestandsschutzOffer(tier: 'plus' | 'ultra' | 'founders'): Promise<BestandsschutzOffer> {
  return apiRequest('/api/membership/bestandsschutz/claim', { method: 'POST', body: { tier } });
}

// ─── Paket S5: Abo-Zentrale "Dein Abo" ────────────────────────────────────
//
// Server-Vertrag: GET /api/membership/overview liefert eine flache Übersicht
// mit Tier/Laufzeit, tagesaktuellen Kontingenten, Wochen-Tranchen, getrennten
// Coin-Quellen, expiring-soon-Hinweis und den letzten 60 Ledger-Zeilen.

export interface MembershipOverviewTranche {
  granted_at: string;
  amount: number;
  expires_at: string | null;
  remaining: number | null;
}

export interface MembershipOverviewExpiring {
  at: string;
  amount: number;
}

export interface MembershipOverviewLedgerRow {
  at: string;
  kind:
    | 'coins_granted'
    | 'coins_spent'
    | 'coins_expired'
    | 'message_free'
    | 'live_minutes'
    | 'background'
    | 'status';
  amount: number;
  label: string;
}

export interface MembershipOverview extends StoreContractFields {
  storeMembership?: ActiveMembership | null;
  tier: MembershipTier;
  label: string;
  status: string;
  duration: MembershipDuration | null;
  provider: string | null;
  period_start: string | null;
  period_end: string | null;
  usable_until: string | null;
  auto_renew_enabled: boolean;
  messages: {
    used_today: number;
    limit_daily: number | null;
    resets_at: string;
  };
  live: {
    minutes_left: number;
    minutes_per_cycle: number;
    cycle_end: string | null;
    coin_per_min: number;
  };
  backgrounds: {
    left: number;
    per_cycle: number;
    cycle_end: string | null;
  };
  status_characters: {
    included: number;
    used_today: number;
  };
  coins: {
    balance_total: number;
    subscription: MembershipOverviewTranche[];
    purchased_balance: number;
    bonus_balance: number;
    next_grant_at: string | null;
    next_grant_amount: number | null;
    expiring_soon: MembershipOverviewExpiring | null;
  };
  ledger: MembershipOverviewLedgerRow[];
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function numberOrZero(value: unknown): number {
  const parsed = numberOrNull(value);
  return parsed === null ? 0 : parsed;
}

export function normalizeMembershipOverview(value: unknown): MembershipOverview {
  const root = (value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {});
  const tier = ['plus', 'ultra', 'founders', 'torchbearer'].includes(String(root.tier))
    ? root.tier as MembershipTier
    : 'free';
  const ledger = Array.isArray(root.ledger) ? root.ledger : [];
  const subscription = Array.isArray((root.coins as Record<string, unknown> | undefined)?.subscription)
    ? ((root.coins as Record<string, unknown>).subscription as unknown[])
    : [];
  return {
    ...storeContractFields(root),
    ...('store_membership' in root ? { storeMembership: parseStoreMembership(root.store_membership && typeof root.store_membership === 'object'
      ? { contract_has_account_id: root.contract_has_account_id, ...record(root.store_membership) } : root.store_membership) } : {}),
    tier,
    label: typeof root.label === 'string' ? root.label : 'Frei',
    status: typeof root.status === 'string' ? root.status : 'none',
    duration: ['week', 'month', 'year', 'custom'].includes(String(root.duration))
      ? root.duration as MembershipDuration
      : null,
    provider: typeof root.provider === 'string' ? root.provider : null,
    period_start: typeof root.period_start === 'string' ? root.period_start : null,
    period_end: typeof root.period_end === 'string' ? root.period_end : null,
    usable_until: typeof root.usable_until === 'string' ? root.usable_until : null,
    auto_renew_enabled: Boolean(root.auto_renew_enabled),
    messages: (() => {
      const m = (root.messages && typeof root.messages === 'object'
        ? root.messages as Record<string, unknown>
        : {});
      return {
        used_today: numberOrZero(m.used_today),
        limit_daily: numberOrNull(m.limit_daily),
        resets_at: typeof m.resets_at === 'string' ? m.resets_at : new Date().toISOString(),
      };
    })(),
    live: (() => {
      const l = (root.live && typeof root.live === 'object'
        ? root.live as Record<string, unknown>
        : {});
      return {
        minutes_left: numberOrZero(l.minutes_left),
        minutes_per_cycle: numberOrZero(l.minutes_per_cycle),
        cycle_end: typeof l.cycle_end === 'string' ? l.cycle_end : null,
        coin_per_min: numberOrZero(l.coin_per_min),
      };
    })(),
    backgrounds: (() => {
      const b = (root.backgrounds && typeof root.backgrounds === 'object'
        ? root.backgrounds as Record<string, unknown>
        : {});
      return {
        left: numberOrZero(b.left),
        per_cycle: numberOrZero(b.per_cycle),
        cycle_end: typeof b.cycle_end === 'string' ? b.cycle_end : null,
      };
    })(),
    status_characters: (() => {
      const s = (root.status_characters && typeof root.status_characters === 'object'
        ? root.status_characters as Record<string, unknown>
        : {});
      return {
        included: numberOrZero(s.included),
        used_today: numberOrZero(s.used_today),
      };
    })(),
    coins: (() => {
      const c = (root.coins && typeof root.coins === 'object'
        ? root.coins as Record<string, unknown>
        : {});
      const expiring = (c.expiring_soon && typeof c.expiring_soon === 'object'
        ? c.expiring_soon as Record<string, unknown>
        : null);
      return {
        balance_total: numberOrZero(c.balance_total),
        subscription: subscription.map((entry) => {
          const row = (entry && typeof entry === 'object' && !Array.isArray(entry)
            ? entry as Record<string, unknown>
            : {});
          return {
            granted_at: typeof row.granted_at === 'string' ? row.granted_at : '',
            amount: numberOrZero(row.amount),
            expires_at: typeof row.expires_at === 'string' ? row.expires_at : null,
            remaining: numberOrNull(row.remaining),
          };
        }),
        purchased_balance: numberOrZero(c.purchased_balance),
        bonus_balance: numberOrZero(c.bonus_balance),
        next_grant_at: typeof c.next_grant_at === 'string' ? c.next_grant_at : null,
        next_grant_amount: numberOrNull(c.next_grant_amount),
        expiring_soon: expiring
          ? {
              at: typeof expiring.at === 'string' ? expiring.at : '',
              amount: numberOrZero(expiring.amount),
            }
          : null,
      };
    })(),
    ledger: ledger.map((entry) => {
      const row = (entry && typeof entry === 'object' && !Array.isArray(entry)
        ? entry as Record<string, unknown>
        : {});
      const kind = String(row.kind || 'coins_spent');
      return {
        at: typeof row.at === 'string' ? row.at : new Date().toISOString(),
        kind: ([
          'coins_granted',
          'coins_spent',
          'coins_expired',
          'message_free',
          'live_minutes',
          'background',
          'status',
        ].includes(kind) ? kind : 'coins_spent') as MembershipOverviewLedgerRow['kind'],
        amount: numberOrZero(row.amount),
        label: typeof row.label === 'string' ? row.label : '',
      };
    }),
  };
}

export async function fetchMembershipOverview(): Promise<MembershipOverview> {
  return normalizeMembershipOverview(await apiRequest<unknown>('/api/membership/overview'));
}

// ─── Paket S10: Empfehlung aus tatsächlicher Nutzung ──────────────────────
//
// GET /api/membership/recommendation liefert eine ehrliche Plus/Ultra-Empfehlung
// aus den letzten 28 Tagen Nutzung (Nur-Plus reicht, wenn keine Ultra-Indikatoren
// vorliegen). `based_on_days` ist immer 28; `reasons` enthält Rohwerte für die
// UI-Zeile. Bei komplett ungenutzten Konten kommt Plus ohne Begründung.

export interface MembershipRecommendationReason {
  key: 'avg_messages_per_day' | 'campfire_minutes_28d' | 'backgrounds_28d' | 'tts_28d';
  value: number;
}

export interface MembershipRecommendation {
  tier: 'plus' | 'ultra' | null;
  reasons: MembershipRecommendationReason[];
  based_on_days: number;
  downgrade_suggested?: boolean;
  // P2 (MiniMax-Audit 20.09.2026): `note: 'unlimited'` markiert die
  // Fackelträger-Antwort. Die UI blendet die Empfehlungszeile in diesem Fall
  // aus — der Nutzer hat bereits unbegrenzten Zugang.
  note?: 'unlimited' | null;
}

export function normalizeRecommendation(value: unknown): MembershipRecommendation {
  const root = record(value);
  const rawTier = root.tier;
  const tier: 'plus' | 'ultra' | null =
    rawTier === 'ultra' ? 'ultra'
    : rawTier === 'plus' ? 'plus'
    : null;
  const rawReasons = Array.isArray(root.reasons) ? root.reasons : [];
  const reasons: MembershipRecommendationReason[] = [];
  for (const entry of rawReasons) {
    const rec = record(entry);
    const key = String(rec.key);
    const value = number(rec.value, 0);
    if (
      ['avg_messages_per_day', 'campfire_minutes_28d', 'backgrounds_28d', 'tts_28d'].includes(key)
      && Number.isFinite(value)
    ) {
      reasons.push({ key: key as MembershipRecommendationReason['key'], value });
    }
  }
  const rawNote = root.note;
  const note: 'unlimited' | null = rawNote === 'unlimited' ? 'unlimited' : null;
  return {
    tier,
    reasons,
    based_on_days: number(root.based_on_days, 28),
    downgrade_suggested: bool(root.downgrade_suggested, false),
    note,
  };
}

export async function fetchMembershipRecommendation(): Promise<MembershipRecommendation> {
  return normalizeRecommendation(await apiRequest<unknown>('/api/membership/recommendation'));
}

export function useMembershipOverview() {
  return useQuery({
    queryKey: ['membership', 'overview'],
    queryFn: fetchMembershipOverview,
    retry: 1,
    staleTime: 60_000,
  });
}

// Re-export für die Übersichtsseite (vermeidet Direktimport von queryKeys).
export const MEMBERSHIP_OVERVIEW_QUERY_KEY = queryKeys.membership.snapshot;
